import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { CardList } from "../../ui/Card";
import Modal from "../../ui/Modal";
import Select from "react-select";
import { AuthContext } from "../../../context/AuthContext";
import { fetchWithAuth } from "../../../utils/api";
import { swalError, swalSuccess } from "../../../utils/swalAlert";
import Loader from "../../ui/Loader";
import Qualified from "../../ui/Announcement/Qualified";
import NotQualified from "../../ui/Announcement/NotQualified";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const StudentClasses = ({ onOpenRenewalModal }) => {
  const { user } = useContext(AuthContext);
  const id = user._id;
  const instituteId = user.instituteId;
  const isUdghosh = user?.category === "UDGHOSH" || user?.institute?.category === "UDGHOSH";

  const navigate = useNavigate();

  const [institute, setInstitute] = useState(null);
  const [classes, setClasses] = useState([]);
  const [hasFetchedClasses, setHasFetchedClasses] = useState(false);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [formError, setFormError] = useState("");
  const [isJoining, setIsJoining] = useState(false);

  // Fetch institute data
  useEffect(() => {
    const fetchInstituteData = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch institute data");
        const data = await response.json();
        setInstitute(data);
      } catch (err) {
        swalError(err.message || "Failed to fetch institute data");
      }
    };
    fetchInstituteData();
  }, [user.instituteId]);

  // Fetch enrolled classes
  const fetchEnrolledClasses = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetchWithAuth(
        `${serverUrl}/api/enrollment/get-all/student/${id}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch enrolled classes"
        );
      }
      const data = await response.json();

      // Assign a fixed background image based on index and filter out Practice Zones
      const enrichedClasses = data
        .filter((cls) => !(cls.class && cls.class.isPracticeZone))
        .map((cls) => ({
          id: cls._id,
          ...cls,
        }));

      setClasses(enrichedClasses);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
      setHasFetchedClasses(true);
    }
  }, [id]);

  // Parse qualification class IDs from env (comma-separated list of 4 class IDs)
  // Parse qualification class IDs from env (comma-separated list of 4 class IDs)
  const qualificationClassIds = useMemo(() => {
    const raw = process.env.REACT_APP_QUALIFICATION_CLASS_IDS || "";
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }, []);

  // Find the enrolled Phase 2 pool class
  const qualifiedClass = useMemo(() => {
    if (!qualificationClassIds.length || !classes.length) return null;
    return classes.find((cls) => {
      const candidates = [
        cls.classId,
        cls.class?._id,
        cls.class?.id,
        cls._id,
        cls.id,
      ]
        .filter(Boolean)
        .map(String);
      return candidates.some((cid) => qualificationClassIds.includes(cid));
    });
  }, [classes, qualificationClassIds]);

  const qualifiedClassId = useMemo(() => {
    if (!qualifiedClass) return null;
    return (
      qualifiedClass.classId ||
      qualifiedClass.class?._id ||
      qualifiedClass._id
    );
  }, [qualifiedClass]);

  // Determine if the student is qualified for Phase 2
  const isQualified = useMemo(() => {
    return Boolean(qualifiedClass);
  }, [qualifiedClass]);

  // Find the primary enrolled Phase 1 pool class (excluding practice classes)
  const firstNonPracticeClass = useMemo(() => {
    if (!classes.length) return null;
    return (
      classes.find((cls) => {
        const c = cls.class || cls;
        return !(
          c.isPractice === true ||
          c.name === "Practice Zone"
        );
      }) || classes[0]
    );
  }, [classes]);

  const firstNonPracticeClassId = useMemo(() => {
    if (!firstNonPracticeClass) return null;
    return (
      firstNonPracticeClass.classId ||
      firstNonPracticeClass.class?._id ||
      firstNonPracticeClass._id
    );
  }, [firstNonPracticeClass]);

  // Fetch available classes for joining
  const fetchAvailableClasses = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetchWithAuth(
        `${serverUrl}/api/class/get-all/institute/${instituteId}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch available classes"
        );
      }
      const data = await response.json();
      // Transform data for react-select
      const transformedClasses = data.map((cls) => {
        return {
          value: cls._id,
          label:
            (!cls.course || cls.course._id === undefined) &&
            (!cls.period || cls.period._id === undefined)
              ? cls
                ? cls.name
                : "N/A"
              : `${
                  cls.period?.term
                    ? cls.period.term.charAt(0).toUpperCase() +
                      cls.period.term.slice(1)
                    : "N/A"
                } ${cls.period?.year || "N/A"} - ${
                  cls.course?.code
                    ? cls.course.code.charAt(0).toUpperCase() +
                      cls.course.code.slice(1)
                    : "N/A"
                }`,
        };
      });

      setAvailableClasses(transformedClasses);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instituteId]);

  useEffect(() => {
    fetchEnrolledClasses();
  }, [fetchEnrolledClasses]);

  // Handle opening the Join Class modal and fetch available classes
  const handleJoinClick = useCallback(() => {
    setIsJoinModalOpen(true);
    fetchAvailableClasses();
  }, [fetchAvailableClasses]);

  // Handle joining a class
  const handleJoinClass = async (e) => {
    e.preventDefault();
    setFormError("");
    setIsJoining(true);

    if (!selectedClass) {
      setFormError("Please select a class to join.");
      setIsJoining(false);
      return;
    }

    const trimmedJoinCode = joinCode.trim();

    if (!trimmedJoinCode) {
      setFormError("Please enter a Enrollment Code.");
      setIsJoining(false);
      return;
    }

    // Pre-validation to check if already enrolled
    const isAlreadyEnrolled = classes.some(
      (cls) => cls._id === selectedClass.value
    );

    if (isAlreadyEnrolled) {
      setFormError(`You are already enrolled in this ${isUdghosh ? "pool" : "class"}.`);
      swalError(
        `You are already enrolled in this ${isUdghosh ? "pool" : "class"}. Please check your enrolled ${isUdghosh ? "pools" : "classes"}.`
      );
      setIsJoining(false);
      return;
    }

    try {
      const response = await fetchWithAuth(`${serverUrl}/api/enrollment/add`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          classId: selectedClass.value,
          code: trimmedJoinCode,
          studentId: id,
        }),
      });
      if (!response) {
        setIsJoining(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        if (
          errorData.message === "Student is already enrolled in this class." ||
          errorData.message === "Class not found."
        ) {
          throw new Error(errorData.message);
        }
        throw new Error(errorData.message || `Failed to join ${isUdghosh ? "pool" : "class"}.`);
      }

      const joinedClass = await response.json();

      if (joinedClass && joinedClass._id) {
        await fetchEnrolledClasses();
        swalSuccess(`Joined ${isUdghosh ? "pool" : "class"} successfully!`);
      } else {
        throw new Error("Invalid class data received.");
      }

      setJoinCode("");
      setSelectedClass(null);
      setIsJoinModalOpen(false);
    } catch (error) {
      setFormError(error.message || `Failed to join ${isUdghosh ? "pool" : "class"}.`);
      swalError(error.message || `Failed to join ${isUdghosh ? "pool" : "class"}.`);
    } finally {
      setIsJoining(false);
    }
  };

  // Prepare cards data for CardList
  const cardsData = useMemo(
    () => [
      ...(user && user.sourceSystem !== "Google Classroom" && institute?.allowStudentClassJoin !== false
        ? [
            {
              id: "action-card",
              title: isUdghosh ? "Join Pool" : "Join Class",
              onAction: handleJoinClick,
              isActionCard: true,
            },
          ]
        : []),
      ...classes.map((cls) => {
        const courseCode = cls.course ? cls.course.code : "N/A";
        const periodTerm = cls.period ? cls.period.term : "N/A";
        const periodYear = cls.period ? cls.period.year : "N/A";
        const className = cls.class ? cls.class.name : "N/A";

        const combinedTitle =
          cls.courseId === undefined && cls.periodId === undefined
            ? className
            : `${
                periodTerm
                  ? periodTerm.charAt(0).toUpperCase() + periodTerm.slice(1)
                  : "N/A"
              } ${periodYear ? periodYear : "N/A"} - ${
                courseCode
                  ? courseCode.charAt(0).toUpperCase() + courseCode.slice(1)
                  : "N/A"
              }`;

        return {
          id: cls.id || cls._id,
          title: combinedTitle,
          badgeCount: cls.pendingTestIds ? cls.pendingTestIds.length : 0,
          description: (
            <>
              <p>
                {cls?.course?.name && (
                  <span>Course Name: {cls.course.name}</span>
                )}
              </p>
              <p>{cls?.class?.type && <span>Type: {cls?.class?.type}</span>}</p>
              {institute && institute.curriculum && <p>Curriculum: {institute.curriculum}</p>}
              <p>
                {cls?.class?.level && <span>Level: {cls?.class?.level}</span>}
              </p>
              <p>
                {cls?.class?.code && (
                  <span>Enrollment Code: {cls?.class?.code}</span>
                )}
              </p>
            </>
          ),
          onClick: () => {
            navigate(`/student/view-class/${cls.classId}`);
          },
        };
      }),
    ],
    [classes, handleJoinClick, navigate, user, institute, isUdghosh]
  );

  return (
    <div>
      <div className="student-home">
        {error && <div className="error-message">{error}</div>}
        {loading && <Loader />}

        <CardList cards={cardsData} />
      </div>

      <Modal isOpen={isJoinModalOpen} onClose={() => setIsJoinModalOpen(false)}>
        <h2>{isUdghosh ? "Join Pool" : "Join Class"}</h2>
        <form onSubmit={handleJoinClass}>
          <div className="form-group">
            <label htmlFor="availableClasses">
              <i className="fa-solid fa-asterisk requiredIcon"></i> {isUdghosh ? "Select Pool" : "Select Class"}
            </label>
            <Select
              id="availableClasses"
              name="availableClasses"
              options={availableClasses}
              value={selectedClass}
              onChange={setSelectedClass}
              isClearable
              placeholder={isUdghosh ? "Select a pool to join" : "Select a class to join"}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="joinCode">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Enrollment
              Code
            </label>
            <input
              type="text"
              id="joinCode"
              name="joinCode"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
              required
              disabled={isJoining}
              placeholder="Enter the enrollment code provided by your instructor"
            />
          </div>

          {formError && <div className="error-message">{formError}</div>}
          {isJoining ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Join
            </button>
          )}
        </form>
      </Modal>

      {/* Conditional Phase 1 Results Announcement Popups */}
      {isUdghosh && hasFetchedClasses && (
        isQualified ? (
          <Qualified
            userName={user?.firstName || ""}
            poolClassId={qualifiedClassId}
            onViewPoolCallback={() => {
              if (qualifiedClassId) {
                navigate(`/student/view-class/${qualifiedClassId}`);
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          />
        ) : (
          <NotQualified
            userName={user?.firstName || ""}
            poolClassId={firstNonPracticeClassId}
            classes={classes}
            onViewPoolCallback={() => {
              if (firstNonPracticeClassId) {
                navigate(`/student/view-class/${firstNonPracticeClassId}`);
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          />
        )
      )}
    </div>
  );
};

export default StudentClasses;
